import React from 'react';

export default function InfoCard({ type, data, equipmentModels, requestsType, actions , viewMode }) {
  const getEquipmentName = () => {
    if (data.request?.equipment_id && equipmentModels[data.request.equipment_id]) return equipmentModels[data.request.equipment_id];
    if (data.equipment_id && equipmentModels[data.equipment_id]) return equipmentModels[data.equipment_id];
    if (data.request?.details) return data.request.details.substring(0, 50);
    return '—';
  };

  const getTypeName = () => {
    if (data.type_id) return requestsType[data.type_id] || '—';
    if (data.request?.type_id) return requestsType[data.request.type_id] || '—';
    return '—';
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusText = (status) => {
    const map = { pending: 'Ожидает', confirmed: 'Подтверждён', completed: 'Выполнен', cancelled: 'Отменён', accepted: 'Принят', rejected: 'Отклонён', new: 'Новый' };
    return map[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      confirmed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      cancelled: 'bg-slate-100 text-slate-600 border-slate-200',
      accepted: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      new: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const getTypeIcon = () => {
    if (type === 'offer') return '💰';
    if (type === 'booking') return '📅';
    if (type === 'request') return '📋';
    return '📄';
  };

  const Field = ({ icon, label, value }) => (
    <div className="flex items-start gap-2">
      <span className="text-slate-400 text-sm">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="text-sm text-slate-800 font-medium break-words">{value || '—'}</div>
      </div>
    </div>
  );

  const renderFields = () => {
    const fields = [
      { icon: '🔧', label: 'Техника', value: getEquipmentName() },
      { icon: '📋', label: 'Тип', value: getTypeName() },
    ];

    if (type === 'booking') {
      fields.push(
        { icon: '📍', label: 'Адрес', value: data.customer_address },
        { icon: '🏙️', label: 'Город', value: data.city || data.request?.city },
        { icon: '📞', label: 'Телефон', value: data.phone_visible !== false ? (data.customer_phone || data.request?.phone_number) : (data.display_phone || '+7 *** *** ** ** — после оплаты') },
        { icon: '📅', label: 'Начало', value: formatDate(data.start_date) },
        { icon: '📅', label: 'Окончание', value: formatDate(data.end_date) },
        { icon: '👤', label: 'Заказчик', value: '#' + data.customer_id },
        { icon: '👤', label: 'Исполнитель', value: '#' + data.owner_id },
        { icon: '💳', label: 'Оплата', value: data.payment_status === 'paid' ? '✅ Оплачено' : data.payment_status === 'unpaid' ? '⏳ Не оплачено' : data.payment_status === 'held' ? '🔒 Заморожено' : '—' }
      );
    }

    if (type === 'offer') {
      fields.push(
        { icon: '👤', label: 'Исполнитель', value: '#' + data.executor_id },
        { icon: '👤', label: 'Заказчик', value: '#' + data.customer_id },
        { icon: '💬', label: 'Комментарий', value: data.comment }
      );
    }

    if (type === 'request') {
      fields.push(
        { icon: '👤', label: 'Создатель', value: '#' + data.user_id },
        { icon: '📅', label: 'Дата заявки', value: formatDate(data.date_requested) },
        { icon: '📍', label: 'Адрес', value: data.address },
        { icon: '🏙️', label: 'Город', value: data.city },
        { icon: '📞', label: 'Телефон', value: data.phone_number },
        { icon: '💰', label: 'Цена/час', value: data.price_per_hour ? data.price_per_hour + ' ₽' : '—' }
      );
    }

    const details = data.comment || data.details || data.request?.details;
    if (details) fields.push({ icon: '💬', label: 'Детали', value: details });

    return fields;
  };

  const photoUrl = data.photo_url || data.request?.photo_url;
  const hasPhoto = photoUrl && photoUrl.length > 0;

  return (
    <div className={'bg-white rounded-2xl border-2 shadow-sm hover:shadow-md transition-all p-5 mb-4 ' + (data.status === 'pending' ? 'border-amber-200' : data.status === 'cancelled' ? 'border-slate-200 opacity-70' : data.status === 'completed' ? 'border-emerald-200' : 'border-slate-200')}>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h4 className="text-base font-bold text-slate-800 m-0 flex items-center gap-2">
          <span className="text-xl">{getTypeIcon()}</span>
          {type === 'offer' && 'Предложение'}
          {type === 'booking' && 'Бронирование'}
          {type === 'request' && 'Заявка'}
          {' #'}{data.id}
        </h4>
        <span className={'px-3 py-1 rounded-full text-xs font-semibold border ' + getStatusColor(data.status)}>
          {getStatusText(data.status)}
        </span>
      </div>

      {/* ✅ Фото — фиксированная высота 200px */}
      {hasPhoto && (
        <div className="mb-4 rounded-xl overflow-hidden border border-slate-200" style={{ height: '200px' }}>
          <img 
            src={photoUrl} 
            alt="Фото техники" 
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        {renderFields().map((field, idx) => <Field key={idx} {...field} />)}
      </div>
      
      
      {/* 💰 Комиссия — вид зависит от роли */}
      {viewMode === 'executor' && (data.payout != null || data.price != null) && (
        <div className="mt-4 p-5 bg-gradient-to-r from-gold to-yellow-400 border-2 border-yellow-600 rounded-2xl shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs text-industrial-bg/80 uppercase font-black tracking-wider">💰 ВЫ ПОЛУЧИТЕ</span>
            <span className="text-3xl font-black text-industrial-bg">
              {Number(data.payout ?? data.price).toLocaleString('ru-RU')} ₽
            </span>
          </div>

          {/* ✅ Путь выплаты */}
          {(() => {
            const card = data.owner_payout_card;
            const phone = data.owner_payout_phone;

            if (!card && !phone) {
              return (
                <div className="mt-3 pt-3 border-t border-gold/30 text-xs text-amber-800 bg-amber-50 -m-4 mt-3 p-3 rounded-b-xl flex items-start gap-2">
                  <span>⚠️</span>
                  <span>
                    Реквизиты не заполнены. Укажите карту или СБП в{' '}
                    <strong>настройках профиля</strong>, чтобы получить деньги.
                  </span>
                </div>
              );
            }

            const maskedCard = card
              ? '**** ' + String(card).replace(/\s/g, '').slice(-4)
              : null;
            const maskedPhone = phone
              ? '+7 *** *** ** ' + String(phone).replace(/\D/g, '').slice(-2)
              : null;

            const display = card
              ? '💳 Карта ' + maskedCard
              : '📱 СБП ' + maskedPhone;

            return (
              <>
                <div className="mt-3 pt-3 border-t border-gold/30 text-xs text-slate-500 flex items-center justify-between">
                  <span>Куда придут деньги:</span>
                  <span className="font-medium text-slate-700">{display}</span>
                </div>
                <div className="text-[11px] text-industrial-bg/70 mt-2 flex items-center gap-1">
                  ⏱ Выплата в течение 1-2 рабочих дней после подтверждения заказчиком
                </div>
              </>
            );
          })()}
        </div>
      )}
      {viewMode === 'customer' && (data.total_price != null || data.customer_price != null) && (
        <div className="mt-4 p-5 bg-gradient-to-r from-gold to-yellow-400 border-2 border-yellow-600 rounded-2xl shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs text-industrial-bg/80 uppercase font-black tracking-wider">💰 СТОИМОСТЬ</span>
            <span className="text-3xl font-black text-industrial-bg">
              {Number(data.total_price ?? data.customer_price).toLocaleString('ru-RU')} ₽
            </span>
          </div>
          <div className="text-[11px] text-industrial-bg/70 mt-1 text-right font-medium">включая сервисный сбор</div>
        </div>
      )}

      {actions && (
        <div className="flex gap-3 flex-wrap pt-4 border-t border-slate-100">
          {actions}
        </div>
      )}
    </div>
  );
}